/**
 * Preferences Utility - Shared user preferences management
 * Issue #500 - Waitlist Polish Features
 * Issue #789 - Booth View Mode Persistence
 * Issue #788 - Booth Match Score Filter
 */

/**
 * User preferences interface
 */
export interface WaitlistPreferences {
  hapticsEnabled: boolean;
  soundEnabled: boolean;
}

/**
 * Booth view mode preference interface
 * Issue #789
 */
export interface BoothViewPreference {
  viewMode: 'all' | 'recommended';
  lastUpdated: number; // Unix timestamp
}

/**
 * Booth match score threshold preference interface
 * Issue #788
 */
export interface BoothMatchScorePreference {
  threshold: number; // 0-100
  lastUpdated: number; // Unix timestamp
}

/**
 * Vendor profile selection preference interface
 * Issue #787
 */
export interface VendorProfilePreference {
  vendorId: number;
  lastUpdated: number; // Unix timestamp
}

/**
 * Local storage key for preferences
 */
export const WAITLIST_PREFERENCES_KEY = 'waitlist_preferences';

/**
 * Local storage key for booth view mode preference
 * Format: booth_view_preference_${userId}
 * Issue #789
 */
export const BOOTH_VIEW_PREFERENCE_KEY_PREFIX = 'booth_view_preference_';

/**
 * Local storage key for booth match score threshold preference
 * Format: booth_match_score_threshold_${userId}
 * Issue #788
 */
export const BOOTH_MATCH_SCORE_THRESHOLD_KEY_PREFIX = 'booth_match_score_threshold_';

/**
 * Local storage key for vendor profile selection preference
 * Format: vendor_profile_preference_${userId}
 * Issue #787
 */
export const VENDOR_PROFILE_PREFERENCE_KEY_PREFIX = 'vendor_profile_preference_';

/**
 * Default preferences
 */
export const DEFAULT_PREFERENCES: WaitlistPreferences = {
  hapticsEnabled: true,
  soundEnabled: true,
};

/**
 * Default booth match score threshold (0 = show all matches)
 * Issue #788
 */
export const DEFAULT_BOOTH_MATCH_SCORE_THRESHOLD = 0;

/**
 * Booth view mode preference expiration (30 days in milliseconds)
 * Issue #789
 */
const BOOTH_VIEW_PREFERENCE_EXPIRATION_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * Booth match score threshold preference expiration (30 days in milliseconds)
 * Issue #788
 */
const BOOTH_MATCH_SCORE_THRESHOLD_EXPIRATION_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * Vendor profile preference expiration (30 days in milliseconds)
 * Issue #787
 */
const VENDOR_PROFILE_PREFERENCE_EXPIRATION_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * Get user preferences from localStorage
 * @returns WaitlistPreferences object
 */
export function getUserPreferences(): WaitlistPreferences {
  try {
    const stored = localStorage.getItem(WAITLIST_PREFERENCES_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return {
        hapticsEnabled: parsed.hapticsEnabled ?? DEFAULT_PREFERENCES.hapticsEnabled,
        soundEnabled: parsed.soundEnabled ?? DEFAULT_PREFERENCES.soundEnabled,
      };
    }
  } catch (error) {
    console.warn('Failed to load waitlist preferences:', error);
  }

  return { ...DEFAULT_PREFERENCES };
}

/**
 * Save user preferences to localStorage
 * @param preferences - Preferences to save
 */
export function saveUserPreferences(preferences: WaitlistPreferences): void {
  try {
    localStorage.setItem(WAITLIST_PREFERENCES_KEY, JSON.stringify(preferences));
  } catch (error) {
    console.warn('Failed to save waitlist preferences:', error);
  }
}

/**
 * Get booth view mode preference for a specific user
 * @param userId - The user ID to get preference for
 * @returns The preferred view mode, or null if not set/expired
 * Issue #789
 */
export function getBoothViewPreference(userId: number): 'all' | 'recommended' | null {
  try {
    const key = `${BOOTH_VIEW_PREFERENCE_KEY_PREFIX}${userId}`;
    const stored = localStorage.getItem(key);

    if (!stored) {
      return null;
    }

    const parsed: BoothViewPreference = JSON.parse(stored);
    const now = Date.now();

    // Validate the parsed data structure
    if (!parsed || typeof parsed.viewMode !== 'string' || typeof parsed.lastUpdated !== 'number') {
      localStorage.removeItem(key);
      return null;
    }

    // Validate viewMode is one of the expected values
    if (parsed.viewMode !== 'all' && parsed.viewMode !== 'recommended') {
      localStorage.removeItem(key);
      return null;
    }

    // Check if preference has expired (30 days)
    if (now - parsed.lastUpdated > BOOTH_VIEW_PREFERENCE_EXPIRATION_MS) {
      // Clean up expired preference
      localStorage.removeItem(key);
      return null;
    }

    return parsed.viewMode;
  } catch (error) {
    console.warn('Failed to load booth view preference:', error);
    return null;
  }
}

/**
 * Save booth view mode preference for a specific user
 * @param userId - The user ID to save preference for
 * @param viewMode - The preferred view mode ('all' or 'recommended')
 * Issue #789
 */
export function saveBoothViewPreference(userId: number, viewMode: 'all' | 'recommended'): void {
  try {
    const key = `${BOOTH_VIEW_PREFERENCE_KEY_PREFIX}${userId}`;
    const preference: BoothViewPreference = {
      viewMode,
      lastUpdated: Date.now(),
    };
    localStorage.setItem(key, JSON.stringify(preference));
  } catch (error) {
    console.warn('Failed to save booth view preference:', error);
    // Fail silently - localStorage may be unavailable (private browsing)
  }
}

/**
 * Clear booth view mode preference for a specific user
 * @param userId - The user ID to clear preference for
 * Issue #789
 */
export function clearBoothViewPreference(userId: number): void {
  try {
    const key = `${BOOTH_VIEW_PREFERENCE_KEY_PREFIX}${userId}`;
    localStorage.removeItem(key);
  } catch (error) {
    console.warn('Failed to clear booth view preference:', error);
  }
}

/**
 * Get booth match score threshold preference for a specific user
 * @param userId - The user ID to get preference for
 * @returns The threshold (0-100), or null if not set/expired
 * Issue #788
 */
export function getBoothMatchScoreThreshold(userId: number): number | null {
  try {
    const key = `${BOOTH_MATCH_SCORE_THRESHOLD_KEY_PREFIX}${userId}`;
    const stored = localStorage.getItem(key);

    if (!stored) {
      return null;
    }

    const parsed: BoothMatchScorePreference = JSON.parse(stored);
    const now = Date.now();

    // Validate the parsed data structure
    if (!parsed || typeof parsed.threshold !== 'number' || typeof parsed.lastUpdated !== 'number') {
      localStorage.removeItem(key);
      return null;
    }

    // Validate threshold is within valid range (0-100)
    if (parsed.threshold < 0 || parsed.threshold > 100) {
      localStorage.removeItem(key);
      return null;
    }

    // Check if preference has expired (30 days)
    if (now - parsed.lastUpdated > BOOTH_MATCH_SCORE_THRESHOLD_EXPIRATION_MS) {
      // Clean up expired preference
      localStorage.removeItem(key);
      return null;
    }

    return parsed.threshold;
  } catch (error) {
    console.warn('Failed to load booth match score threshold:', error);
    return null;
  }
}

/**
 * Save booth match score threshold preference for a specific user
 * @param userId - The user ID to save preference for
 * @param threshold - The threshold value (0-100)
 * Issue #788
 */
export function saveBoothMatchScoreThreshold(userId: number, threshold: number): void {
  try {
    // Validate threshold range
    if (threshold < 0 || threshold > 100) {
      console.warn('Invalid booth match score threshold:', threshold);
      return;
    }

    const key = `${BOOTH_MATCH_SCORE_THRESHOLD_KEY_PREFIX}${userId}`;
    const preference: BoothMatchScorePreference = {
      threshold,
      lastUpdated: Date.now(),
    };
    localStorage.setItem(key, JSON.stringify(preference));
  } catch (error) {
    console.warn('Failed to save booth match score threshold:', error);
    // Fail silently - localStorage may be unavailable (private browsing)
  }
}

/**
 * Clear booth match score threshold preference for a specific user
 * @param userId - The user ID to clear preference for
 * Issue #788
 */
export function clearBoothMatchScoreThreshold(userId: number): void {
  try {
    const key = `${BOOTH_MATCH_SCORE_THRESHOLD_KEY_PREFIX}${userId}`;
    localStorage.removeItem(key);
  } catch (error) {
    console.warn('Failed to clear booth match score threshold:', error);
  }
}

/**
 * Get vendor profile selection preference for a specific user
 * @param userId - The user ID to get preference for
 * @returns The preferred vendor ID, or null if not set/expired
 * Issue #787
 */
export function getVendorProfilePreference(userId: number): number | null {
  try {
    const key = `${VENDOR_PROFILE_PREFERENCE_KEY_PREFIX}${userId}`;
    const stored = localStorage.getItem(key);

    if (!stored) {
      return null;
    }

    const parsed: VendorProfilePreference = JSON.parse(stored);
    const now = Date.now();

    // Validate the parsed data structure
    if (!parsed || typeof parsed.vendorId !== 'number' || typeof parsed.lastUpdated !== 'number') {
      localStorage.removeItem(key);
      return null;
    }

    // Validate vendorId is a positive integer
    if (parsed.vendorId <= 0 || !Number.isInteger(parsed.vendorId)) {
      localStorage.removeItem(key);
      return null;
    }

    // Check if preference has expired (30 days)
    if (now - parsed.lastUpdated > VENDOR_PROFILE_PREFERENCE_EXPIRATION_MS) {
      // Clean up expired preference
      localStorage.removeItem(key);
      return null;
    }

    return parsed.vendorId;
  } catch (error) {
    console.warn('Failed to load vendor profile preference:', error);
    return null;
  }
}

/**
 * Save vendor profile selection preference for a specific user
 * @param userId - The user ID to save preference for
 * @param vendorId - The preferred vendor ID
 * Issue #787
 */
export function saveVendorProfilePreference(userId: number, vendorId: number): void {
  try {
    // Validate vendorId
    if (vendorId <= 0 || !Number.isInteger(vendorId)) {
      console.warn('Invalid vendor ID:', vendorId);
      return;
    }

    const key = `${VENDOR_PROFILE_PREFERENCE_KEY_PREFIX}${userId}`;
    const preference: VendorProfilePreference = {
      vendorId,
      lastUpdated: Date.now(),
    };
    localStorage.setItem(key, JSON.stringify(preference));
  } catch (error) {
    console.warn('Failed to save vendor profile preference:', error);
    // Fail silently - localStorage may be unavailable (private browsing)
  }
}

/**
 * Clear vendor profile selection preference for a specific user
 * @param userId - The user ID to clear preference for
 * Issue #787
 */
export function clearVendorProfilePreference(userId: number): void {
  try {
    const key = `${VENDOR_PROFILE_PREFERENCE_KEY_PREFIX}${userId}`;
    localStorage.removeItem(key);
  } catch (error) {
    console.warn('Failed to clear vendor profile preference:', error);
  }
}
